import { channelsQueryKeys } from '@communities/channels-layer/utils/query-keys.utils'
import { communityUserQueryKeys } from '@communities/community-user-layer/utils/query-keys.utils'
import { leaderboardQueryKeys } from '@communities/leaderboard-layer/utils/query-keys.utils'
import { membersQueryKeys } from '@communities/members-layer/utils/query-keys.utils'
import { postsQueryKeys } from '@communities/posts-layer/utils/query-keys.utils'

import { groupsQueryKeys } from './query-keys.utils'
import type { QueryClient } from '@tanstack/vue-query'

function isUserGroupDetailsQueryKey(query: { queryKey: readonly unknown[] }): boolean {
  const key = query.queryKey as string[]
  return key.length >= 4 && key[0] === 'community-user' && key[2] === 'user-group-details'
}

/**
 * Invalidate TanStack Query caches that depend on auth + current-user↔group membership
 * (e.g. after login). Matches the communities layout login watcher behavior.
 */
export function invalidateCommunitiesMembershipSessionQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    queryKey: ['community-user'],
    predicate: isUserGroupDetailsQueryKey,
  })
  void queryClient.invalidateQueries({ queryKey: channelsQueryKeys.root() })
}

/**
 * After leaving a group while still authenticated: session-level invalidation plus
 * group-scoped lists (posts, members, leaderboard, groups/discovery).
 */
export function invalidateCommunitiesMembershipCachesAfterLeaveGroup(
  queryClient: QueryClient,
  params: { groupId: string; locationId?: string }
): void {
  const { groupId, locationId } = params

  invalidateCommunitiesMembershipSessionQueries(queryClient)

  if (locationId) {
    void queryClient.invalidateQueries({
      queryKey: communityUserQueryKeys.userGroupDetails(groupId, locationId),
    })
  }

  void queryClient.invalidateQueries({ queryKey: communityUserQueryKeys.userGroupsListPrefix() })
  void queryClient.invalidateQueries({ queryKey: postsQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: membersQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groups() })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.pinnedGroups() })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groupScoped(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.discoveryAll() })
}
